import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Business, People } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';

type FaqItem = { q: string; a: string };

const providerFaqs: FaqItem[] = [
  {
    q: 'How much does LawnHub cost for providers?',
    a: 'There is no forced monthly subscription. Platform fees are typically 1% when you bring your own customers onto LawnHub, and 5% for new marketplace leads in the first season (1% on renewals with the same provider). Customers pay card processing separately.',
  },
  {
    q: 'Can I bring my existing lawn care customers onto LawnHub?',
    a: 'Yes. You can onboard your existing customer base and keep those relationships while using CRM, scheduling, crew tools, and automated payments — typically at a 1% platform fee on completed jobs.',
  },
  {
    q: 'Does LawnHub replace tools like Jobber or spreadsheets?',
    a: 'LawnHub is built as a complete ops platform: CRM, scheduling, route optimization, mobile crew app, payments, Financial Dashboard, and optional crew time tracking with payroll-ready export — so many providers can replace spreadsheet chaos or expensive subscription software.',
  },
  {
    q: 'How do I get paid?',
    a: 'Payments are processed after you complete a service. Once the customer payment settles, you receive weekly payouts to your connected Stripe Express account. Track earnings, fees, and payout status in your dashboard.',
  },
  {
    q: 'How do I get new customer leads?',
    a: 'Marketplace leads appear when homeowners request services in your area. You can submit competitive bids; customers compare bids and choose. You only pay a commission when you complete a job — no upfront lead fees or forced subscriptions.',
  },
  {
    q: 'Do you sell my customer or business data?',
    a: 'No. We built LawnHub so your business data works for you — not for us to sell. Your customers, routes, and numbers stay in your account.',
  },
];

const customerFaqs: FaqItem[] = [
  {
    q: 'Do I need to be there for estimates?',
    a: 'No. Providers submit bids based on your service request details and satellite imagery. Compare bids and ratings online without waiting at the property — especially helpful when managing services for family members.',
  },
  {
    q: 'Can I set up services for my loved ones?',
    a: 'Yes. You can manage lawn care for parents or other family properties remotely: request service, compare bids, accept a provider, and handle payments online.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept major credit and debit cards through Stripe. You can save a payment method for recurring services. Seasonal contracts may offer one-time or monthly payment plans.',
  },
  {
    q: 'How are marketplace providers verified?',
    a: 'Providers who bid on new marketplace requests must submit liability insurance for LawnHub verification (and a pesticide applicator license for weed control/fertilizing bids). Providers who bring their own customers are not independently verified the same way for insurance or licenses.',
  },
  {
    q: 'Can I have more than one property on my account?',
    a: 'Yes. Manage multiple properties from one customer account. Each property has its own requests and schedules while sharing a payment method.',
  },
];

const FaqsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'provider' | 'customer'>('provider');
  const faqs = tab === 'provider' ? providerFaqs : customerFaqs;

  return (
    <>
      <SEO
        title="LawnHub FAQs | Providers & Customers"
        description="Frequently asked questions about LawnHub for lawn care providers and homeowners — pricing, onboarding customers, scheduling, payments, and verification."
        keywords="LawnHub FAQ, lawn care software FAQ, provider fees, onboard customers, lawn care payments"
        url="/faqs"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [...providerFaqs, ...customerFaqs].map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        }}
      />

      <Box sx={{ bgcolor: 'grey.50', py: { xs: 5, md: 7 }, borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 800, color: 'primary.main', mb: 1.5, fontSize: { xs: '1.75rem', md: '2.5rem' } }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mb: 3 }}>
            Straight answers for lawn care businesses and the customers they serve.
          </Typography>
          <Tabs
            value={tab === 'provider' ? 0 : 1}
            onChange={(_, v) => setTab(v === 0 ? 'provider' : 'customer')}
            sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minWidth: 160 } }}
          >
            <Tab icon={<Business />} iconPosition="start" label="For Providers" />
            <Tab icon={<People />} iconPosition="start" label="For Customers" />
          </Tabs>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={3}>
          {faqs.map((item) => (
            <Grid item xs={12} md={6} key={item.q}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {item.q}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                  {item.a}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 5, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => navigate(tab === 'provider' ? '/provider-benefits' : '/customer-benefits')}
            sx={{ fontWeight: 'bold' }}
          >
            {tab === 'provider' ? 'More Provider Details' : 'More Customer Details'}
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/get-started')}
            sx={{ bgcolor: '#ff6f00', fontWeight: 'bold', '&:hover': { bgcolor: '#ff8f00' } }}
          >
            Get Started Free
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default FaqsPage;
